import { useState, useEffect } from 'react';

interface UseExitIntentOptions {
  // Sensibilité pour la détection du mouvement vers le haut (en pixels)
  sensitivity?: number;
  // Délai avant de pouvoir déclencher à nouveau l'exit intent (en ms)
  delay?: number;
  // Si true, ne déclenche l'exit intent qu'une seule fois
  onlyOnce?: boolean;
  // Si true, désactive la détection d'exit intent
  disabled?: boolean;
}

/**
 * Hook personnalisé pour détecter l'intention de quitter la page
 * Détecte les mouvements de souris vers le haut de la page et les tentatives de fermeture d'onglet
 */
const useExitIntent = ({
  sensitivity = 20,
  delay = 1000,
  onlyOnce = false,
  disabled = false,
}: UseExitIntentOptions = {}) => {
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [lastExitTime, setLastExitTime] = useState(0);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    if (disabled || (onlyOnce && hasTriggered)) {
      return;
    }

    // Fonction pour détecter le mouvement de la souris vers le haut de la page
    const handleMouseLeave = (e: MouseEvent) => {
      // Si la souris sort par le haut de la page avec une vitesse suffisante
      if (
        e.clientY <= sensitivity &&
        Date.now() - lastExitTime > delay &&
        (!onlyOnce || !hasTriggered)
      ) {
        setShowExitIntent(true);
        setLastExitTime(Date.now());
        if (onlyOnce) {
          setHasTriggered(true);
        }
      }
    };

    // Fonction pour détecter la tentative de fermeture de l'onglet
    const handleBeforeUnload = () => {
      // Ne pas bloquer la navigation - juste afficher la popup interne
      // La popup native du navigateur est trop intrusive et bloque les actions internes
      if (!onlyOnce || !hasTriggered) {
        setShowExitIntent(true);
        if (onlyOnce) {
          setHasTriggered(true);
        }
      }
      // NE PAS appeler preventDefault() pour ne pas bloquer la navigation
    };

    // Ajouter les écouteurs d'événements
    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Nettoyer les écouteurs d'événements
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sensitivity, delay, onlyOnce, disabled, lastExitTime, hasTriggered]);

  // Fonction pour réinitialiser l'état
  const resetExitIntent = () => {
    setShowExitIntent(false);
  };

  return { showExitIntent, resetExitIntent };
};

export default useExitIntent;
